import * as React from 'react'
import { cn } from '../../lib/utils'

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'primary' | 'secondary' | 'white'
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size = 'md', variant = 'primary', ...props }, ref) => {
    const sizeValue = {
      sm: 16,
      md: 24,
      lg: 32,
      xl: 48,
    }[size]

    const strokeWidth = {
      sm: 2,
      md: 2.5,
      lg: 3,
      xl: 4,
    }[size]

    const getColors = () => {
      switch (variant) {
        case 'primary':
          return {
            track: '#e5e7eb', // gray-200
            progress: '#3b82f6', // blue-500
            gradient: ['#3b82f6', '#60a5fa'], // blue-500 to blue-400
          }
        case 'secondary':
          return {
            track: '#e5e7eb', // gray-200
            progress: '#6b7280', // gray-500
            gradient: ['#6b7280', '#9ca3af'], // gray-500 to gray-400
          }
        case 'white':
          return {
            track: 'rgba(255, 255, 255, 0.3)',
            progress: '#ffffff',
            gradient: ['#ffffff', 'rgba(255, 255, 255, 0.8)'],
          }
        default:
          return {
            track: '#e5e7eb',
            progress: '#3b82f6',
            gradient: ['#3b82f6', '#60a5fa'],
          }
      }
    }

    const colors = getColors()
    const center = sizeValue / 2
    const radius = center - strokeWidth / 2
    const circumference = 2 * Math.PI * radius

    return (
      <div ref={ref} className={cn('inline-block', sizeClasses[size], className)} {...props}>
        <svg
          className="animate-spin"
          width={sizeValue}
          height={sizeValue}
          viewBox={`0 0 ${sizeValue} ${sizeValue}`}
        >
          <defs>
            <linearGradient id={`gradient-${variant}-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.gradient[0]} />
              <stop offset="100%" stopColor={colors.gradient[1]} />
            </linearGradient>
          </defs>

          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={colors.track}
            strokeWidth={strokeWidth}
            fill="none"
          />

          {/* Progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={`url(#gradient-${variant}-${size})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * 0.75} // 25% visible
            className="origin-center"
            style={{
              transformOrigin: `${center}px ${center}px`,
            }}
          />
        </svg>
      </div>
    )
  }
)
Spinner.displayName = 'Spinner'

export interface LoadingProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'primary' | 'secondary' | 'white'
  text?: string
  fullScreen?: boolean
}

const Loading = React.forwardRef<HTMLDivElement, LoadingProps>(
  (
    {
      className,
      size = 'lg',
      variant = 'primary',
      text = 'Loading...',
      fullScreen = false,
      ...props
    },
    ref
  ) => {
    if (fullScreen) {
      return (
        <div
          ref={ref}
          className={cn(
            'flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100',
            className
          )}
          {...props}
        >
          <div className="text-center">
            <Spinner size={size} variant={variant} className="mx-auto mb-4" />
            <p className="text-gray-600">{text}</p>
          </div>
        </div>
      )
    }

    return (
      <div ref={ref} className={cn('flex items-center justify-center p-8', className)} {...props}>
        <div className="text-center">
          <Spinner size={size} variant={variant} className="mx-auto mb-2" />
          <p className="text-sm text-gray-600">{text}</p>
        </div>
      </div>
    )
  }
)
Loading.displayName = 'Loading'

export { Spinner, Loading }
